import React, { useEffect, useState } from "react";
import {
  BellIcon,
  CheckCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { fetchNotifications } from "../../features/admin/PostsSlice";
import { fetchCategoryByType } from "../../features/admin/CategorySlice";
import CustomTableComponent from "../../components/CustomTableComponent";
import ApiCustom from "../../utils/ApiCustom";
import { toast } from "react-toastify";

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

function AdminNotificationStatisticCard() {
  const dispatch = useDispatch();
  const userId = localStorage.getItem("userId");

  const { authNotifications: notifications, authNotificationStatus: status } =
    useSelector((state) => state.posts);
  const { categories } = useSelector((state) => state.categories);

  const [approved, setApproved] = useState(0);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch notification posts, categories
  useEffect(() => {
    if (status === "idle") dispatch(fetchNotifications());
    dispatch(fetchCategoryByType("notification,navbar,post,"));
  }, [dispatch, status]);

  // Lọc dữ liệu đúng type notification
  useEffect(() => {
    if (!notifications || !categories) return;
    const filtered = notifications.filter(
      (post) =>
        (userId == 1 ||
          categories.some((category) => category.id === post.categoryId)) &&
        post.type === "notification",
    );
    setTotal(filtered.length);
    setApproved(filtered.filter((p) => p.status === "published").length);
  }, [notifications, categories, userId]);

  return (
    <div className="w-full">
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full sm:w-auto shadow-md flex flex-col items-center text-center p-5 rounded-2xl border border-yellow-100  hover:border-yellow-200 transition-all cursor-pointer"
        onClick={() => setModalOpen(true)}
      >
        <div className="p-3 bg-yellow-100 rounded-full mb-3">
          <BellIcon className="h-6 w-6 text-yellow-600" />
        </div>
        <p className="text-lg text-black">Hoạt động đã duyệt</p>
        <p className="text-3xl font-bold mb-1">
          <span>{approved}</span>
          <span className="text-3xl font-bold"> / </span>
          <span>{total}</span>
        </p>
      </motion.div>
      <NotificationApproveModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          dispatch(fetchNotifications());
        }}
        notifications={
          notifications
            ? notifications.filter((p) => p.type === "notification")
            : []
        }
        categories={categories || []}
        userId={userId}
      />
    </div>
  );
}

// Modal danh sách & duyệt notification
function NotificationApproveModal({
  open,
  onClose,
  notifications,
  categories,
  userId,
}) {
  const [filter, setFilter] = useState("Tất cả");
  const [filteredNotis, setFilteredNotis] = useState([]);
  const [approving, setApproving] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [localNotis, setLocalNotis] = useState([]);

  // Fake local state cho thao tác duyệt (không call api)
  useEffect(() => {
    setLocalNotis(
      notifications
        ? notifications.filter(
            (post) =>
              userId == 1 ||
              categories.some((cat) => cat.id === post.categoryId),
          )
        : [],
    );
  }, [notifications, categories, userId]);

  useEffect(() => {
    setFilteredNotis(
      localNotis.filter((p) =>
        filter === "Tất cả"
          ? true
          : filter === "Đã duyệt"
            ? p.status === "published"
            : p.status !== "published",
      ),
    );
  }, [localNotis, filter]);

  // Duyệt hoặc bỏ duyệt: gọi API
  const handleApprove = async (id, toStatus) => {
    setApproving(id);
    try {
      await ApiCustom.put(`/posts-approve/${id}`, {
        status: toStatus,
      });
      setLocalNotis((prev) =>
        prev.map((p) => (p.id === id ? { ...p, status: toStatus } : p)),
      );
      toast.success(
        toStatus === "published"
          ? "Duyệt hoạt động thành công!"
          : "Bỏ duyệt hoạt động thành công!",
      );
    } catch (err) {
      // ignore
    } finally {
      setApproving(null);
      setConfirmId(null);
    }
  };

  // Chi tiết notification: fetch API by id
  const handleRowClick = async (item) => {
    setDetailLoading(true);
    setDetailId(item.id);
    try {
      const res = await ApiCustom.get(`/posts/${item.id}`);
      setSelected(res?.data?.data || item);
    } catch {
      setSelected(item);
    } finally {
      setDetailLoading(false);
    }
  };

  const headers = [
    {
      key: "title",
      label: "Tiêu đề",
      width: "35%",
      render: (item) => (
        <div className="font-medium whitespace-normal break-words">
          {item.title || "--"}
        </div>
      ),
    },
    {
      key: "category",
      label: "Danh mục",
      width: "20%",
      render: (item) => (
        <div className="whitespace-normal break-words">
          {item.category
            ? item.category.name
            : categories.find((c) => c.id === item.categoryId)?.name || "--"}
        </div>
      ),
    },
    {
      key: "createdAt",
      label: "Ngày tạo",
      width: "15%",
      render: (item) =>
        item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("vi-VN")
          : "--",
    },
    {
      key: "actions",
      label: "Thao tác",
      width: "15%",
      align: "center",
      render: (item) => (
        <div className="flex justify-center">
          {item.status !== "published" ? (
            <button
              className="btn btn-outline btn-success btn-xs flex items-center gap-1"
              disabled={!!approving}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmId({ id: item.id, action: "approve" });
              }}
            >
              <CheckCircleIcon className="w-4 h-4 hidden md:block" />
              Duyệt
            </button>
          ) : (
            <button
              className="btn btn-outline btn-error btn-xs flex items-center gap-1"
              disabled={!!approving}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmId({ id: item.id, action: "unapprove" });
              }}
            >
              <XMarkIcon className="w-4 h-4 hidden md:block" />
              Bỏ duyệt
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          {...animation}
          className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
        >
          <motion.div
            {...animation}
            className="relative bg-white w-full max-w-7xl mx-4 rounded-xl p-6 border  overflow-y-auto shadow-xl"
            style={{ maxHeight: "90vh" }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="flex justify-between items-center mb-4 mr-4">
              <h2 className="text-xl font-bold">Danh sách hoạt động</h2>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border rounded px-3 py-2  focus:outline-none focus:border-yellow-400"
              >
                <option value="Tất cả">Tất cả</option>
                <option value="Chờ duyệt">Chờ duyệt</option>
                <option value="Đã duyệt">Đã duyệt</option>
              </select>
            </div>
            <CustomTableComponent
              headers={headers}
              data={filteredNotis}
              onRowClick={confirmId ? () => {} : handleRowClick}
            />
            <NotificationDetailModal
              open={!!detailId}
              loading={detailLoading}
              onClose={() => {
                setDetailId(null);
                setSelected(null);
              }}
              noti={selected}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Modal chi tiết notification (fetch detail)
function NotificationDetailModal({
  open,
  loading,
  onClose,
  noti,
  registrationCount,
  joinedCount,
}) {
  if (!open) return null;
  // Hàm format date giống file mẫu
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("vi-VN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "--";

  return (
    <AnimatePresence>
      <motion.div
        {...animation}
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
      >
        <motion.div
          {...animation}
          className="relative bg-white rounded-2xl max-w-7xl w-full p-8 shadow-xl border border-yellow-100"
          style={{ maxHeight: "90vh", overflow: "auto" }}
          initial={{ scale: 0.96 }}
          animate={{ scale: 1, transition: { duration: 0.2 } }}
          exit={{ scale: 0.96, transition: { duration: 0.15 } }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          {loading ? (
            <div className="py-10 text-center text-gray-500">
              Đang tải chi tiết...
            </div>
          ) : !noti ? (
            <div className="py-10 text-center text-gray-500">
              Không tìm thấy hoạt động
            </div>
          ) : (
            <>
              <div className="mb-1 text-sm text-gray-500 font-medium">
                Danh mục:{" "}
                <span className="text-yellow-700 font-semibold">
                  {noti.category?.name || "—"}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-2 mb-6">
                <BellIcon className="h-6 w-6 text-yellow-500" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {noti.title}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700">
                <div className="space-y-2">
                  <div>
                    Trạng thái:{" "}
                    <span
                      className={`font-medium ${
                        noti.status === "published"
                          ? "text-green-700"
                          : "text-yellow-700"
                      }`}
                    >
                      {noti.status === "published" ? "Đã duyệt" : "Chờ duyệt"}
                    </span>
                  </div>
                  <div>Ngày tổ chức: {formatDate(noti.eventStartTime)}</div>
                  <div>Ngày tạo: {formatDate(noti.createdAt)}</div>
                </div>
                <div className="space-y-2">
                  <div>
                    Bắt đầu đăng ký:{" "}
                    <span className="text-blue-700 font-medium">
                      {formatDate(noti.regisStartTime)}
                    </span>
                  </div>
                  <div>
                    Kết thúc đăng ký:{" "}
                    <span className="text-blue-700 font-medium">
                      {formatDate(noti.regisEndTime)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-6 border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Nội dung chi tiết
                </h3>
                <div
                  className="text-sm leading-relaxed text-gray-800 bg-gray-50 p-4 rounded-lg border"
                  style={{
                    maxHeight: 320,
                    overflowY: "auto",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: noti.content || "<i>Không có nội dung.</i>",
                  }}
                />
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default AdminNotificationStatisticCard;
